import { ComponentType, SVGProps } from 'react'

import { type PortableTextBlock, stegaClean } from 'next-sanity'

import { buttonSizeClasses } from '@/app/components/Button'
import PortableText from '@/app/components/PortableText'
import Video, { hasVideo } from '@/app/components/Video'
import { getHeadingClasses } from '@/app/config/headingStyles'
import { TRACKING_LOCATIONS } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'
import { generateElementId } from '@/app/utils/text'
import DecorHero1Bottom from '@/public/images/decorHero1Bottom.svg'
import DecorHero1Top from '@/public/images/decorHero1Top.svg'
import DecorHero2Bottom from '@/public/images/decorHero2Bottom.svg'
import DecorHero2Top from '@/public/images/decorHero2Top.svg'
import DecorHero3Bottom from '@/public/images/decorHero3Bottom.svg'
import DecorHero3Top from '@/public/images/decorHero3Top.svg'
import DecorHero4Bottom from '@/public/images/decorHero4Bottom.svg'
import DecorHero4Top from '@/public/images/decorHero4Top.svg'
import { HeroSection as HeroSectionProps } from '@/sanity.types'

type HeroProps = {
  block: HeroSectionProps & { _key?: string }
  index: number
  pageId?: string
  pageType?: string
}

type DecorAlign = 'left' | 'right'

type DecorItem = {
  Component: ComponentType<SVGProps<SVGSVGElement>>
  align: DecorAlign
  width: number
  height: number
}

const decorConfig: Record<
  Exclude<HeroSectionProps['decor'], 'none'>,
  { top: DecorItem; bottom: DecorItem }
> = {
  '1': {
    top: { Component: DecorHero1Top, align: 'right', width: 126, height: 209 },
    bottom: { Component: DecorHero1Bottom, align: 'left', width: 157, height: 190 },
  },
  '2': {
    top: { Component: DecorHero2Top, align: 'left', width: 99, height: 104 },
    bottom: { Component: DecorHero2Bottom, align: 'right', width: 127, height: 182 },
  },
  '3': {
    top: { Component: DecorHero3Top, align: 'left', width: 126, height: 209 },
    bottom: { Component: DecorHero3Bottom, align: 'right', width: 100, height: 100 },
  },
  '4': {
    top: { Component: DecorHero4Top, align: 'left', width: 126, height: 205 },
    bottom: { Component: DecorHero4Bottom, align: 'right', width: 98, height: 103 },
  },
}

const getPositionClasses = (align: DecorAlign) =>
  align === 'left'
    ? 'bottom-xl lg:bottom-xl left-0 origin-bottom-left -translate-x-[16px] md:-translate-x-[24px] lg:-translate-x-[40px]'
    : 'top-xl lg:top-xl right-0 origin-top-right translate-x-[16px] md:translate-x-[24px] lg:translate-x-[40px]'

const SHARED_CLASSES = 'absolute pointer-events-none z-10 scale-[0.53] md:scale-[0.68] lg:scale-100'

const renderDecor = (item: DecorItem, position: 'top' | 'bottom') => (
  <item.Component
    className={cn(
      SHARED_CLASSES,
      position === 'top' ? '!bottom-auto top-[10%]' : '!top-auto bottom-[10%]',
      getPositionClasses(item.align),
    )}
    width={item.width}
    height={item.height}
  />
)

export default function HeroSection({ block, pageId, pageType }: HeroProps) {
  const blockDecor = stegaClean(block.decor)
  const config = blockDecor !== 'none' ? decorConfig[blockDecor] : null
  const showVideo = hasVideo(block.video)

  return (
    <div
      className={cn(
        'relative overflow-hidden pt-10xl lg:pt-11xl',
        !showVideo && 'pb-10xl lg:pb-11xl',
      )}
    >
      <div className="content-container">
        {config && (
          <>
            {renderDecor(config.top, 'top')}
            {renderDecor(config.bottom, 'bottom')}
          </>
        )}
        {block?.content?.length && (
          <PortableText
            className={cn(
              'flex flex-col items-center justify-center text-center',
              'mx-auto max-w-[222px] lg:max-w-[600px]',
            )}
            tracking={{ location: TRACKING_LOCATIONS.HERO }}
            buttonClassName={buttonSizeClasses({ lg: 'lg' })}
            value={block.content as PortableTextBlock[]}
            documentId={pageId}
            documentType={pageType}
            parentPath={block._key ? `pageBuilder[_key=="${block._key}"].content` : undefined}
            componentOverrides={{
              block: {
                h2: ({ children }) => {
                  const id = generateElementId(children)
                  return (
                    <h1
                      id={id}
                      data-heading-id={id}
                      data-anchor-expose="true"
                      className={cn(
                        'group relative',
                        getHeadingClasses('h2'),
                        'mt-3xl lg:mt-5xl mb-xl lg:mb-3xl',
                      )}
                    >
                      {children}
                    </h1>
                  )
                },
              },
            }}
          />
        )}
        {showVideo && (
          <div className="mt-3xl md:mt-5xl lg:mt-7xl mx-auto max-w-[1010px]">
            <Video video={block.video} className="rounded-md lg:rounded-lg overflow-hidden" />
          </div>
        )}
      </div>
    </div>
  )
}
